import type { Appearance, Palette } from "@picnic-field/renderer/appearance";

/**
 * 3D 미리보기 공장 — 기본은 C-02 렌더러(@picnic-field/renderer)를 늦게 불러온다(three.js 를 첫 화면 번들에서 뺌).
 * 테스트(jsdom 은 WebGL 이 없음)는 BootDeps.createPreview 로 가짜를 끼운다.
 */
export interface PreviewRequest {
  glbUrl: string;
  appearance: Appearance | null;
  palette: Palette | null;
  interactive: boolean;
  autoRotate: boolean;
  quality: "high" | "low";
}

export interface PreviewLike {
  readonly ready: Promise<{ tintedMeshes: number }>;
  setAppearance(appearance: Appearance | null, palette?: Palette | null): unknown;
  dispose(): void;
}

export type PreviewFactory = (
  canvas: HTMLCanvasElement,
  req: PreviewRequest,
) => Promise<PreviewLike>;

/**
 * Draco 디코더 — 빌드는 three 가 import.meta.url 로 싣는 디코더를 번들러가 자산으로 복사한다.
 * 개발 서버는 사전 번들 때문에 그 URL 이 깨져서 vite 플러그인이 /draco/ 로 준다.
 */
export const DRACO_PATH: string | undefined = import.meta.env.DEV
  ? `${import.meta.env.BASE_URL}draco/`
  : undefined;

export const rendererPreview: PreviewFactory = async (canvas, req) => {
  const { createPreview } = await import("@picnic-field/renderer");
  return createPreview(canvas, { ...req, dracoPath: DRACO_PATH });
};

// 스냅샷 응답의 glbUrl(자산 버전 행 기준)을 먼저 쓰고, 없으면 올리기 때 받은 미리보기 URL 을 세션 동안 기억해 쓴다.
const GLB_KEY = (slug: string, version: string) => `pf.glb.${slug}@${version}`;

export function rememberGlbUrl(slug: string, version: string, url: string): void {
  try {
    window.sessionStorage.setItem(GLB_KEY(slug, version), url);
  } catch {
    /* 저장소를 못 쓰면 썸네일만 자리표시로 */
  }
}

export function glbUrlFor(s: {
  modelSlug: string;
  glbVersion: string;
  glbUrl?: string;
}): string | null {
  if (s.glbUrl) return s.glbUrl;
  try {
    return window.sessionStorage.getItem(GLB_KEY(s.modelSlug, s.glbVersion));
  } catch {
    return null;
  }
}
